import React, { useState, useEffect } from 'react';
import { ESnapshotExists, ENotice, ERate } from 'lib/types';
import { requestDisplayParameters } from 'lib/indesign/request';
import { getRelevantRateString, getRelevantRateDescription } from 'lib/pricing';
import { ELabels, NoticeType } from 'lib/enums';
import { getFirebaseContext } from 'utils/firebase';
import { getIndesignServerClient } from 'utils/indesign';
import { TextAreaField } from 'lib/components/TextAreaField';
import { MAX_MEMO_LENGTH } from 'lib/types/invoices';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import { TextField } from 'lib/components/TextField';
import { DisplayParams } from 'lib/types/notice';

type InvoiceFormMemoProps = {
  noticeSnap: ESnapshotExists<ENotice>;
  rateSnap: ESnapshotExists<ERate>;
  terms: string;
  customMemo: string;
  setCustomMemo: (memo: string) => void;
  purchaseOrderNumber: string;
  setPurchaseOrderNumber: (poNumber: string) => void;
  currency: string;
  customId: string;
  setCustomId: (customId: string) => void;
};

const boldRates = (rate: ERate, displayParams?: DisplayParams) => {
  if (!rate.bold_words && !rate.line_with_bold_words) return;
  if (rate.bold_words) return `Bolded Words: ${displayParams?.boldWords}`;
  if (rate.line_with_bold_words)
    return `Bolded Lines: ${displayParams?.nonTableBoldedLines}`;
};

function InvoiceFormMemo({
  noticeSnap,
  rateSnap,
  terms,
  customMemo,
  setCustomMemo,
  currency,
  purchaseOrderNumber,
  setPurchaseOrderNumber,
  customId,
  setCustomId
}: InvoiceFormMemoProps) {
  const [displayParams, setDisplayParams] = useState(
    noticeSnap.data().displayParams
  );
  const rate = rateSnap.data();
  const { columns } = noticeSnap.data();

  const fetchDisplayParams = async () => {
    // NOTE: It's unclear why we're re-fetching display parameters here for non-display notices rather than trusting the value on the notice.
    if (noticeSnap.data().noticeType !== NoticeType.display_ad.value) {
      try {
        const displayParameters = await requestDisplayParameters(
          getFirebaseContext(),
          await getIndesignServerClient(),
          noticeSnap,
          (window as any).DOMParser
        );
        setDisplayParams(displayParameters);
      } catch (error) {
        logAndCaptureException(
          ColumnService.PAYMENTS,
          error,
          'Error fetching display parameters for notice during invoice creation',
          {
            noticeId: noticeSnap.id
          }
        );
      }
    }
  };

  useEffect(() => {
    void fetchDisplayParams();
  }, []);

  if (!displayParams || !rate || !columns) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg p-3">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg px-4 py-5">
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="font-bold">{ELabels.reference_id.label}</dt>
          <dd>{noticeSnap.data().referenceId}</dd>
        </div>
        <div>
          <dt className="font-bold">Notice ID</dt>
          <dd>{noticeSnap.id}</dd>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            id={ELabels.publisher_id.key}
            labelText={ELabels.publisher_id.label}
            size="xs"
            value={customId}
            onChange={setCustomId}
          />
          <TextField
            id="purchase-order-number"
            size="xs"
            labelText="Purchase order number"
            maxLength={256} // Abitrary just to avoid excessively long strings
            value={purchaseOrderNumber}
            onChange={value => setPurchaseOrderNumber(value)}
          />
        </div>
        <TextAreaField
          id="custom-memo"
          labelText="Custom memo"
          rows={3}
          value={customMemo}
          maxLength={MAX_MEMO_LENGTH}
          onChange={value => setCustomMemo(value)}
        />
        <div>
          <dt className="font-bold">Terms</dt>
          <dd dangerouslySetInnerHTML={{ __html: terms }}></dd>
        </div>
        <div>
          <dt className="font-bold">Rate</dt>
          <dd>
            {rate.description} {' - '}{' '}
            {getRelevantRateDescription(
              rate,
              noticeSnap.data().publicationDates.length,
              currency
            )}
            <br></br>
            {getRelevantRateString(rate, displayParams, columns)}
            {' / '}
            <br />
            {boldRates(rate, displayParams)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export default InvoiceFormMemo;
